import { ChevronDown, Puzzle } from "lucide-react"
import { useState } from "react"

import TextLink from "@/components/text-link"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { INTEGRATION_REGISTRY } from "@/lib/integration-registry"
import { cn } from "@/lib/utils"
import { adminAgentPath } from "@/routes"
import type {
  AccountIntegrationAgent,
  AccountIntegrationDisplay,
  AgentResource,
} from "@/types"

function isConnected(integration: AccountIntegrationDisplay): boolean {
  const { payload } = integration
  return !!(payload?.email ?? payload?.api_key_hint ?? payload?.connected_at)
}

function StatusDot({ active }: { active: boolean }) {
  return (
    <span
      className={cn(
        "inline-block h-2 w-2 rounded-full",
        active ? "bg-green-500" : "bg-amber-500",
      )}
    />
  )
}

function titleCase(str: string) {
  return str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

function connectionDetail(
  integration: AccountIntegrationDisplay,
): string | null {
  const { payload } = integration
  const parts: string[] = []

  if (payload?.email) parts.push(payload.email as string)

  if (payload?.connected_at) {
    const date = new Date(payload.connected_at as string)
    parts.push(`Connected ${date.toLocaleDateString()}`)
  }

  return parts.length > 0 ? parts.join(" \u00b7 ") : "Not connected"
}

function pluralize(count: number, singular: string): string {
  return `${count} ${count === 1 ? singular : `${singular}s`}`
}

interface ActionGroup {
  action: string
  countLabel: string
  searchable: boolean
  resources: AgentResource[]
}

function groupResourcesByAction(
  resources: AgentResource[],
  kind: string,
): ActionGroup[] {
  const registry = INTEGRATION_REGISTRY[kind]
  if (!registry) return []

  const typeMap = new Map(registry.resourceTypes.map((rt) => [rt.value, rt]))

  const actionMap = new Map<string, ActionGroup>()

  for (const resource of resources) {
    const meta = typeMap.get(resource.resource_type)
    if (!meta) continue

    if (!actionMap.has(meta.action)) {
      actionMap.set(meta.action, {
        action: meta.action,
        countLabel: meta.countLabel,
        searchable: meta.searchable,
        resources: [],
      })
    }
    actionMap.get(meta.action)!.resources.push(resource)
  }

  return Array.from(actionMap.values())
}

function hasUnmappedResources(
  resources: AgentResource[],
  kind: string,
): boolean {
  const registry = INTEGRATION_REGISTRY[kind]
  if (!registry) return false

  const typeMap = new Map(registry.resourceTypes.map((rt) => [rt.value, rt]))

  return resources.some((r) => {
    const meta = typeMap.get(r.resource_type)
    return meta?.searchable && !r.external_id
  })
}

function integrationNeedsAttention(
  integration: AccountIntegrationDisplay,
): boolean {
  if (!isConnected(integration)) return true
  return integration.agents.some((agent) =>
    hasUnmappedResources(agent.resources, integration.kind),
  )
}

function ResourceActionGroup({ group }: { group: ActionGroup }) {
  const [open, setOpen] = useState(true)

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium">
        <span>
          {group.action} ({pluralize(group.resources.length, group.countLabel)})
        </span>
        <ChevronDown
          className={cn(
            "text-muted-foreground h-3 w-3 transition-transform",
            open && "rotate-180",
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="mt-1 space-y-0.5 pl-3 text-xs">
          {group.resources.map((r, i) =>
            group.searchable ? (
              <li key={i} className="flex items-center gap-1.5">
                <StatusDot active={!!r.external_id} />
                <span
                  className={
                    r.external_id
                      ? "text-muted-foreground"
                      : "text-amber-600 dark:text-amber-400"
                  }
                >
                  {r.label ?? r.name}
                  {r.external_id ? "" : " — Not mapped"}
                </span>
              </li>
            ) : (
              <li key={i} className="text-muted-foreground">
                {r.label ?? r.name}
              </li>
            ),
          )}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  )
}

function AgentRow({
  agent,
  kind,
  connected,
}: {
  agent: AccountIntegrationAgent
  kind: string
  connected: boolean
}) {
  const [open, setOpen] = useState(true)
  const actionGroups = groupResourcesByAction(agent.resources, kind)
  const unmapped = hasUnmappedResources(agent.resources, kind)

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="bg-muted/50 rounded-lg px-3 py-2.5">
        <CollapsibleTrigger className="flex w-full items-center gap-2 text-left">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <TextLink href={adminAgentPath({ id: agent.uid })}>
              <span className="text-sm">{agent.name}</span>
            </TextLink>
            <StatusDot active={connected && !unmapped} />
          </div>
          <ChevronDown
            className={cn(
              "text-muted-foreground h-4 w-4 shrink-0 transition-transform",
              open && "rotate-180",
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          {actionGroups.length > 0 ? (
            <div className="mt-2 space-y-1.5">
              {actionGroups.map((ag) => (
                <ResourceActionGroup key={ag.action} group={ag} />
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground pt-1 text-xs">
              No resources configured
            </p>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

function IntegrationCard({
  integration,
}: {
  integration: AccountIntegrationDisplay
}) {
  const [open, setOpen] = useState(false)
  const registry = INTEGRATION_REGISTRY[integration.kind]
  const icon = registry?.icon ?? <Puzzle className="h-5 w-5" />
  const label = registry?.label ?? titleCase(integration.kind)
  const connected = isConnected(integration)
  const detail = connectionDetail(integration)
  const needsAttention = integrationNeedsAttention(integration)

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-lg border bg-white p-3 dark:bg-transparent">
        <CollapsibleTrigger className="flex w-full items-center gap-3 text-left">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md">
            {icon}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{label}</span>
              <StatusDot active={!needsAttention} />
            </div>
            <span className="text-muted-foreground text-xs">{detail}</span>
          </div>
          <ChevronDown
            className={cn(
              "text-muted-foreground mr-3 h-4 w-4 shrink-0 transition-transform",
              open && "rotate-180",
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          {integration.agents.length > 0 ? (
            <div className="mt-3 space-y-2">
              {integration.agents.map((agent) => (
                <AgentRow
                  key={agent.uid}
                  agent={agent}
                  kind={integration.kind}
                  connected={connected}
                />
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground mt-3 text-xs">
              No agents connected
            </p>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

export default function AccountIntegrationsDisplay({
  integrations,
}: {
  integrations: AccountIntegrationDisplay[]
}) {
  const attentionCount = integrations.filter((i) =>
    integrationNeedsAttention(i),
  ).length
  const connectedCount = integrations.length - attentionCount

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-3">
        <h2 className="text-base font-semibold">Integrations</h2>
        <div className="text-muted-foreground flex items-center gap-3 text-xs">
          {connectedCount > 0 && (
            <span className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
              {connectedCount} connected
            </span>
          )}
          {attentionCount > 0 && (
            <span className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-full bg-amber-500" />
              {attentionCount} needs attention
            </span>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {integrations.map((integration) => (
          <IntegrationCard key={integration.uid} integration={integration} />
        ))}
      </div>
    </section>
  )
}
